import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState } from 'react';
import { useOffline } from '@/contexts/OfflineContext';
import { getFromOfflineStore } from '@/lib/offlineDb';
import { useAuth } from '@/contexts/AuthContext';
import { createRealtimeChannelName, logRealtimeIssue, removeRealtimeChannel } from '@/lib/realtime';

export const useServiceLogs = (clientId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { } = useAuth();
  const [demoServiceLogs, setDemoServiceLogs] = useState<any[]>([]);
  const { isOnline, queueOfflineMutation, cacheData } = useOffline();

  // Set up real-time subscription (skip for demo mode)
  useEffect(() => {
    if (!isOnline) return;
    
    console.log('Setting up service logs realtime subscription');

    try {
      const channel = supabase
        .channel(createRealtimeChannelName('service-logs-changes'))
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'service_logs'
          },
          (payload) => {
            console.log('New service log inserted:', payload);
            queryClient.invalidateQueries({ queryKey: ['service-logs'] });
            queryClient.invalidateQueries({ queryKey: ['all-services-summary'] });
            
            const newLog = payload.new as any;
            toast({
              title: 'Service Logged',
              description: `${newLog.service_type} service recorded`,
              duration: 3000
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'service_logs'
          },
          (payload) => {
            console.log('Service log updated:', payload);
            queryClient.invalidateQueries({ queryKey: ['service-logs'] });
            queryClient.invalidateQueries({ queryKey: ['all-services-summary'] });
          }
        )
        .subscribe((_status, error) => {
          if (error) logRealtimeIssue('Service logs', error);
        });

      return () => {
        console.log('Cleaning up service logs realtime subscription');
        removeRealtimeChannel(channel);
      };
    } catch (error) {
      logRealtimeIssue('Service logs', error);
    }
  }, [queryClient, toast, isOnline]);

  const { data: serviceLogs = [], isLoading } = useQuery({
    queryKey: ['service-logs', clientId],
    queryFn: async () => {
      if (!isOnline) {
        console.log('Offline: Loading service logs from cache');
        const cached = await getFromOfflineStore<any>('service_logs');
        return clientId ? cached.filter(log => log.client_id === clientId) : cached;
      }

      let query = supabase
        .from('service_logs')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Cache data for offline use
      if (data) {
        await cacheData('service_logs', data);
      }
      
      return data || [];
    },
    enabled: !!clientId || clientId === undefined,
    staleTime: isOnline ? 30000 : Infinity
  });

  const createServiceLog = useMutation({
    mutationFn: async (logData: {
      clientId: string;
      serviceType: 'metro' | 'laundry' | 'meal' | 'juice' | 'breakfast' | 'lunch' | 'dinner' | 'milk';
      quantity?: number;
      notes?: string;
      siteId?: string | null;
    }) => {
      const id = crypto.randomUUID();
      const logEntry = {
        id,
        client_id: logData.clientId,
        service_type: logData.serviceType,
        quantity: logData.quantity || 1,
        notes: logData.notes,
        created_at: new Date().toISOString()
      };

      if (!isOnline) {
        // Queue for sync when back online
        await queueOfflineMutation('service_logs', 'insert', logEntry);
        toast({
          title: 'Saved Offline',
          description: 'Service log will sync when back online',
        });
        return logEntry;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Resolve site_id from client if not supplied — required for cross-view RLS visibility
      let resolvedSiteId: string | null = logData.siteId ?? null;
      if (!resolvedSiteId) {
        const { data: clientRow } = await supabase
          .from('clients')
          .select('site_id')
          .eq('id', logData.clientId)
          .single();
        resolvedSiteId = clientRow?.site_id || null;
      }

      const { data, error } = await supabase
        .from('service_logs')
        .insert([{
          ...logEntry,
          provided_by: user.id,
          site_id: resolvedSiteId,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-logs'] });
      queryClient.invalidateQueries({ queryKey: ['all-services-summary'] });
      queryClient.invalidateQueries({ queryKey: ['client-service-logs', variables.clientId] });
      queryClient.invalidateQueries({ queryKey: ['client-activity-logs', variables.clientId] });
      toast({
        title: 'Success',
        description: 'Service logged successfully'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to log service'
      });
    }
  });

  const deleteServiceLog = useMutation({
    mutationFn: async ({ id, clientId: cid }: { id: string; clientId?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('service_logs')
        .update({ deleted_at: new Date().toISOString(), deleted_by: user.id })
        .eq('id', id);
      if (error) throw error;
      // Activity log
      try {
        await supabase.from('user_activity_logs').insert({
          user_id: user.id,
          action_type: 'delete_service',
          action_details: { service_log_id: id, client_id: cid || null },
        });
      } catch (e) {
        console.warn('activity log failed', e);
      }
      return { id, clientId: cid };
    },
    onSuccess: ({ clientId: cid }) => {
      queryClient.invalidateQueries({ queryKey: ['service-logs'] });
      queryClient.invalidateQueries({ queryKey: ['all-services-summary'] });
      if (cid) {
        queryClient.invalidateQueries({ queryKey: ['client-service-logs', cid] });
        queryClient.invalidateQueries({ queryKey: ['client-activity-logs', cid] });
      }
      toast({ title: 'Service Removed', description: 'The service entry has been removed.' });
    },
    onError: (error: any) => {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to remove service' });
    },
  });

  return {
    serviceLogs,
    isLoading,
    createServiceLog: createServiceLog.mutate,
    deleteServiceLog: deleteServiceLog.mutate,
  };
};
